import React, { useState } from 'react'
import { Modal, Button, Dropdown } from 'react-bootstrap'
import { FaUserGroup, FaPhone, FaCalendar, FaFilter} from 'react-icons/fa6'
import './Reservations.scss'
import { confirmReservation, cancelReservation, markReservationAsAttended } from '../../../../../../../crud/clients_api/client_reservations';
import ModalArriveClientReserv from './components/ModalArriveClientReserv/ModalArriveClientReserv';
import ModalCreateReservationV2 from './components/ModalCreateReservationV2/ModalCreateReservationV2';

export interface Reservation {
  reservation_id: number
  client_name: string
  phone_number: string
  email?: string
  init: number  // in quarter-hours
  n_people: number
  mesa_num: number
  mesa_id: number
  tag?: string  // New field for table tag
  confirmed: boolean
  approved: boolean
  attended: boolean
  cancelled: boolean
  arrived: boolean
}


interface ReservationsProps {
  reservationsToday?: Reservation[]
  filteredReservationsInAHour?: Reservation[]
  setReservationsToday?: (reservations: Reservation[]) => void
  setFilteredReservationsInAHour?: (reservations: Reservation[]) => void
  salones?: any[] // Reemplazar con el tipo adecuado si se desea tipado estricto
  setSalones?: React.Dispatch<React.SetStateAction<any[]>> // Usar tipado de setter de useState
  fetchReservations?: () => void;
}

const timeFromInit = (q: number) => {
  const h = Math.floor(q / 4)
  const m = (q % 4) * 15
  return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}`
}

type ReservationStatus = 'all' | 'pending' | 'confirmed' | 'arrived' | 'attended' | 'cancelled'

const getStateLabel = (r: Reservation) => {
  if (r.cancelled) return 'Cancelada'
  if (r.attended) return 'Atendida'
  if (r.arrived) return 'Llegó'
  if (r.confirmed) return 'Confirmada'
  if (r.approved) return 'Aprobada'
  return 'Por confirmar'
}

const getStateClass = (r: Reservation) => {
  if (r.cancelled) return 'cancelled'
  if (r.attended) return 'attended'
  if (r.arrived) return 'arrived'
  if (r.confirmed) return 'confirmed'
  if (r.approved) return 'approved'
  return 'pending'
}

const getReservationStatus = (r: Reservation): ReservationStatus => {
  if (r.cancelled) return 'cancelled'
  if (r.attended) return 'attended'
  if (r.arrived) return 'arrived'
  if (r.confirmed) return 'confirmed'
  return 'pending'
}

const Reservations: React.FC<ReservationsProps> = ({ 
  reservationsToday, 
  filteredReservationsInAHour, 
  setReservationsToday, 
  setFilteredReservationsInAHour, 
  setSalones, 
  fetchReservations,
  salones }) => {
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<ReservationStatus[]>(['pending', 'confirmed', 'arrived'])

  // modal
  const [showModal, setShowModal] = useState(false)
  const [active, setActive] = useState<Reservation | null>(null)
  // modal para nueva reservación
  const [showCreateModal, setShowCreateModal] = useState(false)
  // modal para llegada/cancelación
  const [showArriveModal, setShowArriveModal] = useState(false);
  const [arriveReservationId, setArriveReservationId] = useState<number | null>(null);
  const [arriveMesaId, setArriveMesaId] = useState<number | null>(null);

  // Usar reservationsToday si está presente, sino fakeReservations
  const baseReservations = reservationsToday ?? [];
  const sorted = baseReservations.slice().sort((a,b)=>a.init-b.init)
  
  // Real-time search and status filter
  const filtered = sorted.filter(r=>{
    // Filter by status
    const currentStatus = getReservationStatus(r)
    if (!statusFilter.includes(currentStatus)) return false;
    
    // Search across multiple fields
    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim()
      const timeStr = timeFromInit(r.init)
      const name = r.client_name.toLowerCase()
      const phone = r.phone_number.toLowerCase()
      const email = (r.email || '').toLowerCase()
      const tag = (r.tag || '').toLowerCase()
      const mesaNum = `mesa ${r.mesa_num}`.toLowerCase()
      
      const matches = 
        name.includes(query) ||
        phone.includes(query) ||
        email.includes(query) ||
        timeStr.includes(query) ||
        tag.includes(query) ||
        mesaNum.includes(query)
      
      if (!matches) return false
    }
    
    return true
  })

  // Toggle status filter
  const toggleStatusFilter = (status: ReservationStatus) => {
    if (status === 'all') {
      setStatusFilter(['pending', 'confirmed', 'arrived', 'attended', 'cancelled'])
    } else {
      setStatusFilter(prev => {
        if (prev.includes(status)) {
          const newFilters = prev.filter(s => s !== status)
          return newFilters.length > 0 ? newFilters : prev
        } else {
          return [...prev, status]
        }
      })
    }
  }

  const openModal = (r: Reservation) => {
    setActive(r);
    setShowModal(true);
  }
  const closeModal = () => {
    setActive(null)
    setShowModal(false)
  }

  // Action handlers for reservation state changes
  const handleConfirm = async (r: Reservation, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await confirmReservation(r.reservation_id)
      const updatedReservation = { ...r, confirmed: true }
      updateReservationInState(updatedReservation)
    } catch (error) {
      console.error('Error confirming reservation:', error)
    }
  }

  const handleMarkArrived = async (r: Reservation, e: React.MouseEvent) => {
    e.stopPropagation()
    setArriveReservationId(r.reservation_id)
    setArriveMesaId(r.mesa_id)
    setShowArriveModal(true)
  }

  const handleMarkAttended = async (r: Reservation, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await markReservationAsAttended(r.reservation_id)
      const updatedReservation = { ...r, attended: true }
      updateReservationInState(updatedReservation)
      
      // Update table state
      if (setSalones && salones) {
        setSalones(salones.map(salon => ({
          ...salon,
          mesas: salon.mesas.map((mesa: any) =>
            mesa.id === r.mesa_id ? { ...mesa, state: true } : mesa
          )
        })))
      }
    } catch (error) {
      console.error('Error marking reservation as attended:', error)
    }
  }

  const handleCancel = async (r: Reservation, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await cancelReservation(r.reservation_id)
      
      // Immediately update mesa state to unblock it
      if (setSalones && salones) {
        setSalones(salones.map(salon => ({
          ...salon,
          mesas: salon.mesas.map((mesa: any) =>
            mesa.id === r.mesa_id ? { ...mesa, state: true } : mesa
          )
        })))
      }
      
      // Update local reservation state
      const updatedReservation = { ...r, cancelled: true }
      updateReservationInState(updatedReservation)
      
      // Refresh reservations from backend to ensure consistency
      if (fetchReservations) {
        await fetchReservations()
      }
    } catch (error) {
      console.error('Error cancelling reservation:', error)
    }
  }

  const updateReservationInState = (updatedReservation: Reservation) => {
    if (setReservationsToday && reservationsToday) {
      setReservationsToday(reservationsToday.map(r => 
        r.reservation_id === updatedReservation.reservation_id ? updatedReservation : r
      ))
    }
    if (setFilteredReservationsInAHour && filteredReservationsInAHour) {
      setFilteredReservationsInAHour(filteredReservationsInAHour.map(r => 
        r.reservation_id === updatedReservation.reservation_id ? updatedReservation : r
      ))
    }
  }
  // abrir/cerrar modal de nueva reservación
  const openCreateModal = () => setShowCreateModal(true)
  const closeArriveModal = () => {
    setArriveReservationId(null);
    setArriveMesaId(null);
    setShowArriveModal(false);
  };

  // Función para actualizar la lista de reservaciones tras crear una nueva
  const handleReservationCreated = () => {
    if (typeof window !== 'undefined' && fetchReservations) {
      fetchReservations();
    }
  };

  // Handlers para actualizar el estado local tras acción en el modal
  const handleArrived = () => {
    if (arriveReservationId && setReservationsToday && reservationsToday) {
      setReservationsToday(reservationsToday.map(r =>
        r.reservation_id === arriveReservationId ? { ...r, arrived: true } : r
      ));
    }
    if (arriveReservationId && setFilteredReservationsInAHour && filteredReservationsInAHour) {
      setFilteredReservationsInAHour(filteredReservationsInAHour.map(r =>
        r.reservation_id === arriveReservationId ? { ...r, arrived: true } : r
      ));
    }
  };
  const handleCancelled = () => {
    if (arriveReservationId && setReservationsToday && reservationsToday) {
      setReservationsToday(reservationsToday.map(r =>
        r.reservation_id === arriveReservationId ? { ...r, cancelled: true } : r
      ));
    }
    if (arriveReservationId && setFilteredReservationsInAHour && filteredReservationsInAHour) {
      setFilteredReservationsInAHour(filteredReservationsInAHour.map(r =>
        r.reservation_id === arriveReservationId ? { ...r, cancelled: true } : r
      ));
    }
    // Table state will be automatically updated by useReservationsManager effect
  };

  return (
    <>
      <div className="reservations">
        {/* — Search Bar and Filters */}
        <div className="reservations__search">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por nombre, teléfono, hora, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Limpiar búsqueda"
              >
                ×
              </button>
            )}
          </div>
          
          <Dropdown className="status-filter-dropdown">
            <Dropdown.Toggle variant="none" className="status-filter-btn">
              <FaFilter />
              <span>Estado</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="status-filter-menu">
              <div className="filter-option">
                <input
                  type="checkbox"
                  id="filter-all"
                  checked={statusFilter.length === 5}
                  onChange={() => toggleStatusFilter('all')}
                />
                <label htmlFor="filter-all">Todas</label>
              </div>
              <div className="filter-option">
                <input
                  type="checkbox"
                  id="filter-pending"
                  checked={statusFilter.includes('pending')}
                  onChange={() => toggleStatusFilter('pending')}
                />
                <label htmlFor="filter-pending">Por confirmar</label>
              </div>
              <div className="filter-option">
                <input
                  type="checkbox"
                  id="filter-confirmed"
                  checked={statusFilter.includes('confirmed')}
                  onChange={() => toggleStatusFilter('confirmed')}
                />
                <label htmlFor="filter-confirmed">Confirmadas</label>
              </div>
              <div className="filter-option">
                <input
                  type="checkbox"
                  id="filter-arrived"
                  checked={statusFilter.includes('arrived')}
                  onChange={() => toggleStatusFilter('arrived')}
                />
                <label htmlFor="filter-arrived">Llegó</label>
              </div>
              <div className="filter-option">
                <input
                  type="checkbox"
                  id="filter-attended"
                  checked={statusFilter.includes('attended')}
                  onChange={() => toggleStatusFilter('attended')}
                />
                <label htmlFor="filter-attended">Atendidas</label>
              </div>
              <div className="filter-option">
                <input
                  type="checkbox"
                  id="filter-cancelled"
                  checked={statusFilter.includes('cancelled')}
                  onChange={() => toggleStatusFilter('cancelled')}
                />
                <label htmlFor="filter-cancelled">Canceladas</label>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* — scrollable list */}
        <div className="reservations__list">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaCalendar /></div>
              <div className="empty-message">No hay reservaciones</div>
              <div className="empty-description">Crea una nueva reservación para gestionar la llegada de clientes</div>
            </div>
          ) : (
            filtered.map(r=>(
              <div key={r.reservation_id} className="reservations__card" onClick={() => openModal(r)}>
                {/* Row 1: Name, Mesa, Time */}
                <div className="reservation-row-1">
                  <div className="reservations__name">{r.client_name}</div>
                  <div className="reservations__mesa">{r.tag || `Mesa ${r.mesa_num}`}</div>
                  <div className="reservations__time">{timeFromInit(r.init)}</div>
                </div>
              
              {/* Row 2: Personal Info Badges */}
              <div className="reservation-row-2">
                <div className="detail-badge">
                  <FaUserGroup />
                  <span>{r.n_people}</span>
                </div>
                <div className="detail-badge">
                  <FaPhone />
                  <span>{r.phone_number}</span>
                </div>
              </div>
              
              {/* Row 3: State Badge and Action Buttons */}
              <div className="reservation-row-3">
                <span className={`reservations__state-badge ${getStateClass(r)}`}>
                  {getStateLabel(r)}
                </span>
                
                {/* Action buttons based on current state */}
                <div className="reservation-actions">
                  {/* Not confirmed yet - show confirm button */}
                  {!r.confirmed && !r.cancelled && (
                    <button
                      className="action-btn confirm-btn"
                      onClick={(e) => handleConfirm(r, e)}
                      title="Confirmar reserva"
                    >
                      Confirmar
                    </button>
                  )}
                  
                  {/* Confirmed but not arrived - show mark arrived button */}
                  {r.confirmed && !r.arrived && !r.cancelled && (
                    <button
                      className="action-btn arrive-btn"
                      onClick={(e) => handleMarkArrived(r, e)}
                      title="Marcar como llegado"
                    >
                      Llegó
                    </button>
                  )}
                  
                  {/* Arrived but not attended - show mark attended button */}
                  {r.arrived && !r.attended && !r.cancelled && (
                    <button
                      className="action-btn attend-btn"
                      onClick={(e) => handleMarkAttended(r, e)}
                      title="Marcar como atendido"
                    >
                      Atendido
                    </button>
                  )}
                  
                  {/* Cancel button - show for all non-cancelled and non-attended */}
                  {!r.cancelled && !r.attended && (
                    <button
                      className="action-btn cancel-btn"
                      onClick={(e) => handleCancel(r, e)}
                      title="Cancelar reserva"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {/* — footer */}
        <div className="reservations__footer">
          <button className="reservations__new-button" onClick={openCreateModal}> 
            + Reservación
          </button>
        </div>
      </div>

      {/* — Reservation Details Modal */}
      {active && (
        <Modal show={showModal} onHide={closeModal} centered className="reservation-details-modal">
          <Modal.Header>
            <div className="form-modal-title-container">
              <h2 className="form-modal-title">Detalles de Reservación</h2>
            </div>
            <button 
              className="modal-close-button"
              onClick={closeModal}
              aria-label="Cerrar"
            >
              ×
            </button>
          </Modal.Header>
          <Modal.Body>
            <div className="reservation-detail-item">
              <strong>Cliente:</strong> {active.client_name}
            </div>
            <div className="reservation-detail-item">
              <strong>Teléfono:</strong> {active.phone_number}
            </div>
            {active.email && (
              <div className="reservation-detail-item">
                <strong>Email:</strong> {active.email}
              </div>
            )}
            <div className="reservation-detail-item">
              <strong>Mesa:</strong> {active.tag || `Mesa ${active.mesa_num}`}
            </div>
            <div className="reservation-detail-item">
              <strong>Hora:</strong> {timeFromInit(active.init)}
            </div>
            <div className="reservation-detail-item">
              <strong>Personas:</strong> {active.n_people}
            </div>
            <div className="reservation-detail-item">
              <strong>Estado:</strong> <span className={`state-label ${getStateClass(active)}`}>{getStateLabel(active)}</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="form-modal-footer-custom">
              <Button variant="secondary" className="form-button-cancel" onClick={closeModal}>Cerrar</Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      <ModalCreateReservationV2
        show={showCreateModal}
        setShowCreateModal={setShowCreateModal}
        salones={salones}
        handleReservationCreated={handleReservationCreated}
      />
      {/* Modal para llegada/cancelación de cliente */}
      <ModalArriveClientReserv
        show={showArriveModal}
        onClose={closeArriveModal}
        reservationId={arriveReservationId || 0}
        mesa_id={arriveMesaId || 0} // Pasar el ID de la reserva para actualizar la mesa
        onArrived={handleArrived}
        onCancelled={handleCancelled}
        setSalones={setSalones} // Pasar setSalones al modal para actualizar el estado de las mesas
      />
    </>
  )
}

export default Reservations
